import { Account, Category } from "../merchantSalesTypes"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useMoney } from "../../../../../currency/useMoney"
import React, { useMemo } from "react"
import { MRT_ColumnDef } from "material-react-table"
import { toDinero } from "../../../../../currency/currencyLib"
import { ApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { COLUMN_SIZE, ColumnConfig, columnConfigAlignEnd } from "../../../../../../shared/constants/table/config"

type DetailedTableProps = { category: Category; isInitialLoading: boolean; isFetching: boolean; isError: boolean }

const useStyles = makeStyles()(() => ({
    tablePaper: {
        boxShadow: "none",
        borderRadius: 0,
    },
    tableHeadCell: {
        border: "none",
        color: tailwindPalette.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: tailwindPalette.slate[50],
    },
}))

export const DetailedTable = ({ category, isError, isFetching, isInitialLoading }: DetailedTableProps) => {
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    const columns = useMemo<MRT_ColumnDef<Account>[]>(
        () => [
            {
                header: category.name,
                accessorKey: "name",
                size: COLUMN_SIZE.S,
                footer: l10n.getString("supplier-merchant-sales-detailed-table-total-footer-column"),
            },
            {
                header: l10n.getString("supplier-merchant-sales-detailed-table-sales-column"),
                accessorFn: (row) => moneyFactory.formatAsNumber(toDinero(row.bookedSales)),
                size: COLUMN_SIZE.M,
                ...(columnConfigAlignEnd as ColumnConfig<Account>),
                Footer: () => {
                    return moneyFactory.formatAsNumber(toDinero(category.totalBookedSales))
                },
                muiTableFooterCellProps: {
                    align: "right",
                },
            },
        ],
        [l10n, moneyFactory],
    )

    const rowData = useMemo(() => (category.accounts as Account[]) ?? [], [category.accounts])

    return (
        <ApiQueryTable
            isLoading={isInitialLoading}
            isFetching={isFetching}
            isError={isError}
            data={rowData}
            columns={columns}
            enableRowActions={false}
            renderTopToolbar={false}
            renderBottomToolbar={false}
            enableColumnActions={false}
            muiTablePaperProps={{ className: classes.tablePaper }}
            muiTableHeadCellProps={{ className: classes.tableHeadCell }}
            muiTableHeadRowProps={{ className: classes.tableHeadRow }}
        />
    )
}
